import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImportStatusStore } from "../../../App/State/Stores/ImportStatusStore";
import { useImportCandidateCSVMutation } from "../../../App/Queries/ImportQueries";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import DropdownMenu from "../../../App/Components/DropdownMenu/DropdownMenu";

export const GroupManageCandidatesButton = ({
  isArchived = false,
  groupId,
  handleOpenAddToGroupDialog,
  userSelected,
  selectedCandidatesCount,
  handleOpenMoveToGroupDialog,
}: {
  isArchived: boolean;
  groupId: string;
  handleOpenAddToGroupDialog: () => void;
  userSelected: boolean;
  selectedCandidatesCount: number;
  handleOpenMoveToGroupDialog: () => void;
}) => {
  const navigate = useNavigate();
  const setShowImportModal = useImportStatusStore((state) => state.setShowImportModal);
  const showImportStatusText = useImportStatusStore((state) => state.showImportStatusText);
  const { isLoading: isUploadLoading } = useImportCandidateCSVMutation();
  const importCandidatesButtonAccess = useFeaturePermission(AppFeatureLookup.CandidatesImportCandidatesByCsv);
  const importDisabled = useMemo(
    () => isArchived || isUploadLoading || !importCandidatesButtonAccess,
    [isArchived, isUploadLoading, importCandidatesButtonAccess],
  );

  let tooltipMessage = "Import candidates by CSV";
  if (showImportStatusText || isUploadLoading) {
    tooltipMessage = "Please wait for the current import to finish";
  } else if (!importCandidatesButtonAccess) {
    tooltipMessage = crmLiteUpgradeMessage;
  } else if (isArchived) {
    tooltipMessage = "Cannot import candidates to an archived group";
  }

  const accessAddToGroupButton = useFeaturePermission(AppFeatureLookup.GroupAddCandidatesToGroupButton);
  let addToGroupTooltip = "Add Candidates to Group";
  if (!accessAddToGroupButton) {
    addToGroupTooltip = crmLiteUpgradeMessage;
  } else if (isArchived) {
    addToGroupTooltip = "Cannot add candidates to an archived group";
  }

  return (
    <DropdownMenu
      buttonLabel="Manage Candidates"
      tooltipText="Manage candidate options"
      menuDataTest="candidates-dropdown-button"
      sx={{ height: "100%", width: "100%" }}
      menuItems={[
        {
          label: "Import Candidates",
          onClick: () => (importDisabled ? null : setShowImportModal(true)),
          disabled: importDisabled,
          title: tooltipMessage,
          dataTest: "open-csv-modal-button",
        },
        {
          label: "View Imports",
          onClick: () => navigate(`/candidategroups/${groupId}/imports`),
          dataTest: "view-imports-button",
        },
        {
          label: "Add Candidates to Group",
          onClick: () => (!accessAddToGroupButton || isArchived ? null : handleOpenAddToGroupDialog()),
          disabled: !accessAddToGroupButton || isArchived,
          title: addToGroupTooltip,
          dataTest: "addtogroup-openbutton",
          "aria-controls": "addtogroup-dialog",
          "aria-haspopup": "true",
        },
        {
          label: "Move Candidates to Group",
          onClick: () => (selectedCandidatesCount === 0 ? null : handleOpenMoveToGroupDialog()),
          disabled: !userSelected || selectedCandidatesCount === 0,
          dataTest: "move-candidates-button",
        },
      ]}
    />
  );
};
